import { create } from "zustand";
import { Profile } from "../models/Profile";
import { Project } from "../models/Project";
import {
  addContactToProject,
  createProject,
  deleteProject,
  getAllProjects,
  getProject,
  updateProject,
  addDirectory,
  updateDirectory,
  deleteDirectory,
  addFileToDirectory,
  updateFile,
  moveFile,
  deleteFile,
  getRootDirectory,
} from "../repositories/ProjectsRepository";
import { Contact } from "../models/Contact";
import { Directory } from "../models/Directory";
import { File as FileEntity } from "../models/File";

export interface ProjectStore {
  projects?: Project[];

  fetchProjects: (user: Profile) => Promise<Project[] | undefined>;
  fetchProject: (
    user: Profile,
    projectId: number,
  ) => Promise<Project | undefined>;

  addProject: (user: Profile, project: Project) => Promise<number | undefined>;
  editProject: (
    user: Profile,
    project: Project,
    projectId: number,
  ) => Promise<number | undefined>;
  removeProject: (
    user: Profile,
    projectId: number,
  ) => Promise<number | undefined>;

  addContactToProject: (
    user: Profile,
    projectId: number,
    contact: Contact,
  ) => Promise<number | undefined>;

  getRoot: (
    user: Profile,
    projectId: number,
  ) => Promise<Directory | undefined>;

  addDirectory: (
    user: Profile,
    parentDirectoryId: number,
    name: string,
  ) => Promise<Directory | undefined>;

  updateDirectory: (
    user: Profile,
    directoryId: number,
    name: string,
  ) => Promise<Directory | undefined>;

  removeDirectory: (
    user: Profile,
    directoryId: number,
  ) => Promise<number | undefined>;

  addFile: (
    user: Profile,
    directoryId: number,
    file: File,
  ) => Promise<File | undefined>;

  updateFile: (
    user: Profile,
    fileId: number,
    content: {
      base64: string;
      mimeType: string;
      fileName: string;
    },
  ) => Promise<FileEntity | undefined>;

  moveFile: (
    user: Profile,
    fileId: number,
    directoryId: number,
  ) => Promise<FileEntity | undefined>;

  deleteFile: (user: Profile, fileId: number) => Promise<number | undefined>;
}

export const useProject = create<ProjectStore>((set) => ({
  fetchProjects: (user) => {
    return getAllProjects(set, user);
  },
  fetchProject: (user, id) => {
    return getProject(user, id);
  },
  addProject: (user, project) => {
    return createProject(user, project);
  },
  addContactToProject: (user, projectId, contact) => {
    return addContactToProject(user, projectId, contact);
  },
  editProject: (user, project, projectId) => {
    return updateProject(user, project, projectId);
  },
  removeProject: (user, projectId) => {
    return deleteProject(user, projectId);
  },
  getRoot: async (user, projectId) => {
    return await getRootDirectory(user, projectId);
  },
  addDirectory: async (user, parentDirectoryId, name) => {
    return await addDirectory(user, parentDirectoryId, name);
  },
  updateDirectory: async (user, directoryId, name) => {
    return await updateDirectory(user, directoryId, name);
  },
  removeDirectory: async (user, directoryId) => {
    return await deleteDirectory(user, directoryId);
  },
  addFile: async (user, directoryId, file) => {
    return await addFileToDirectory(user, directoryId, file);
  },
  updateFile: async (user, fileId, content) => {
    return await updateFile(user, fileId, content);
  },
  moveFile: async (user, fileId, directoryId) => {
    return await moveFile(user, fileId, directoryId);
  },
  deleteFile: async (user, fileId) => {
    return await deleteFile(user, fileId);
  },
}));
